import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import NumbersOutlinedIcon from '@mui/icons-material/NumbersOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import BookOutlinedIcon from '@mui/icons-material/BookOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import Tooltip from '@mui/material/Tooltip';
import Card from '../../components/Card/Card';
import Spinner from '../../components/Spinner';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import { Context } from '../../AuthContext';
import { useCategory } from '../../CategoryContext';

import BackButton from '../../components/BackButton';

import AlertDialog from '../../components/AlertDialog';
import { useDeleteContent } from './useDeleteContent';

const InventorPage = ({ category = 'inventor' }) => {
  const { updateCount, isEditor } = useCategory();
  const { user } = useContext(Context);
  const { handleDelete } = useDeleteContent();
  const theme = useTheme();
  const activeButtonStyle = {
    fontSize: '16px',
    margin: '10px',
    width: '100%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
    },
  };

  const [item, setItem] = useState({});
  const [show, setShow] = useState();
  const [loading, setLoading] = useState(true);

  const { vertical_id, id } = useParams();
  const navigate = useNavigate();

  const icons = {
    inventors: <PeopleOutlinedIcon className="detail-icon" style={{ color: 'green' }} />,
    inventor: <PeopleOutlinedIcon className="detail-icon" style={{ color: 'green' }} />,
    publication_number: <BookOutlinedIcon className="detail-icon" />,
    publication_date: <CalendarMonthOutlinedIcon className="detail-icon" />,
  };

  const urlIcons = {
    resume: (
      <img
        src={`${process.env.PUBLIC_URL}/peopleIcons/cv.svg`}
        alt="resume"
        className="detail-icon clickable"
      />
    ),
    LinkedIn: (
      <img
        src={`${process.env.PUBLIC_URL}/peopleIcons/linkedin.svg`}
        alt="LinkedIn logo"
        className="detail-icon clickable"
      />
    ),
    ResearchGate: (
      <img
        src={`${process.env.PUBLIC_URL}/peopleIcons/ResearchGate.svg`}
        alt="ResearchGate logo"
        className="detail-icon clickable"
      />
    ),
    GoogleScholar: (
      <img
        src={`${process.env.PUBLIC_URL}/peopleIcons/googleScholar.svg`}
        alt="GoogleScholar logo"
        className="detail-icon clickable"
      />
    ),
    ORCID: (
      <img
        src={`${process.env.PUBLIC_URL}/peopleIcons/orcid.svg`}
        alt="ORCID logo"
        className="detail-icon clickable"
      />
    ),
    BlueSky: (
      <img
        src={`${process.env.PUBLIC_URL}/peopleIcons/BlueSky.svg`}
        alt="BlueSky logo"
        className="detail-icon clickable"
      />
    ),
    organization_url: (
      <img
        src={`${process.env.PUBLIC_URL}/peopleIcons/website.svg`}
        alt="Homepage logo"
        className="detail-icon clickable"
      />
    ),
  };

  useEffect(() => {
    if (!user) return;

    const getItem = async () => {
      setLoading(true);
      const item = await fetchItem();
      if (!item) return;
      setItem(item.data);
      document.title = item.data.name + ' - The Simple Platform';
      setShow(item.data.show);
      setLoading(false);
    };
    getItem();
  }, [id, user, vertical_id]);

  const fetchItem = async () => {
    const token = await user.getIdToken();
    const url =
      process.env.REACT_APP_FLASK_WEBSERVER +
      `get_${category}_details?id=${id}&vertical_id=${vertical_id}`;

    const itemRequest = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (itemRequest.status === 403) {
      navigate('/forbidden', { replace: true });
      return null;
    }
    const item = await itemRequest.json();

    const bookmark_status_endpoint =
      process.env.REACT_APP_FLASK_WEBSERVER + `get_bookmark_id/${item?.data?._id}`;
    const bookmarkRequest = await fetch(bookmark_status_endpoint, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const bookmark = bookmarkRequest.ok ? await bookmarkRequest.json() : { isBookmarked: false };

    const result = {
      ...item,
      data: {
        ...item.data,
        isBookmarked: bookmark.isBookmarked,
      },
    };

    return result;
  };

  function editContent(event) {
    event.stopPropagation();
    navigate('edit');
  }

  const hideContent = async (event) => {
    event.stopPropagation();
    if (!user) return;

    const isChecked = !show;
    if (isChecked) {
      updateCount('people', 1);
    } else {
      updateCount('people', -1);
    }
    setShow(isChecked);
    const token = await user.getIdToken();

    const res = await fetch(
      process.env.REACT_APP_FLASK_WEBSERVER +
        'change_visibility?id=' +
        item._id +
        '&collection=' +
        category +
        's&vertical_id=' +
        vertical_id,
      {
        method: 'PATCH',
        headers: {
          'Content-type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ show: isChecked }),
      }
    );
    await res.json();
  };

  const toggleBookmark = async (event, itemId, category) => {
    event.stopPropagation();
    setItem({ ...item, isBookmarked: !item.isBookmarked });

    try {
      let vid = vertical_id;
      const token = await user.getIdToken();

      await fetch(`${process.env.REACT_APP_FLASK_WEBSERVER}toggle_bookmark`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user.uid,
          item_id: itemId,
          category: `${category}s`,
          vertical_id: vid,
        }),
      });
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
    }
  };

  return loading ? (
    <Spinner />
  ) : (
    <div style={{ width: '75%', textAlign: 'left' }}>
      <BackButton />
      <h4 style={{ color: '#596274' }}>INVENTOR</h4>

      <div className="detail-card">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {item?.image_url ? (
              <img
                src={item.image_url}
                alt="Profile"
                style={{
                  height: '128px',
                  width: '128px',
                  marginRight: '20px',
                  marginBottom: '20px',
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
              />
            ) : (
              <AccountCircleOutlinedIcon
                style={{ height: '128px', width: '128px', paddingRight: '20px' }}
              />
            )}
            {item?.name && <h2 className="title">{item.name}</h2>}
          </div>

          {item?.status === 'approved' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: 0,
                margin: 0,
              }}
            >
              {isEditor && item?.status === 'approved' && (
                <div style={{ display: 'flex', alignItems: 'center', padding: 0, margin: 0 }}>
                  {show ? (
                    <Tooltip title="Visible to Members">
                      <VisibilityOutlinedIcon
                        style={{ color: '#356AC3' }}
                        className="detail-icon clickable"
                        onClick={(event) => hideContent(event)}
                        aria-label="Content is visible"
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Hidden To Members">
                      <VisibilityOffOutlinedIcon
                        style={{ color: '#356AC3' }}
                        className="detail-icon clickable"
                        onClick={(event) => hideContent(event)}
                        aria-label="Content is hidden"
                      />
                    </Tooltip>
                  )}
                  <FontAwesomeIcon
                    color="#356AC3"
                    icon={faPenToSquare}
                    className="detail-icon clickable"
                    onClick={(event) => editContent(event)}
                  ></FontAwesomeIcon>
                  <AlertDialog
                    id={item._id}
                    action={() =>
                      handleDelete({
                        id: item._id,
                        collection: category + 's',
                        verticalId: vertical_id,
                        bookmarked: item?.isBookmarked,
                      })
                    }
                    index={-1}
                    dialogTitle={`Delete ${category}: ${item?.name}`}
                    dialogContentText={`This action cannot be undone. This will permanently delete the ${category} and remove the data from our servers.`}
                    color="blue"
                  />
                </div>
              )}
              {item?.status === 'approved' &&
                (item?.isBookmarked ? (
                  <BookmarkIcon
                    className="detail-icon clickable"
                    onClick={(e) => toggleBookmark(e, item._id, `${category}`)}
                    style={{ color: '#356AC3' }}
                  />
                ) : (
                  <BookmarkBorderIcon
                    className="detail-icon clickable"
                    onClick={(e) => toggleBookmark(e, item._id, category)}
                    style={{ color: '#356AC3' }}
                  />
                ))}
            </div>
          )}
        </div>

        {item?.affiliations && (
          <div className="detail-item-">
            <AccountBalanceOutlinedIcon className="detail-icon-" style={{ color: '#AB6EF9' }} />
            {item.affiliations}
          </div>
        )}

        {item?.department && (
          <div className="detail-item-">
            <AccountTreeOutlinedIcon className="detail-icon-" />
            {item.department}
          </div>
        )}

        {item?.program && (
          <div className="detail-item-">
            <AccountTreeOutlinedIcon className="detail-icon-" />
            {item.program}
          </div>
        )}

        {item?.email && (
          <div className="detail-item-">
            <AlternateEmailOutlinedIcon className="detail-icon-" style={{ color: '#4C90F6' }} />
            {item.email}
          </div>
        )}

        {item.organic_results && item.organic_results.length > 0 && (
          <div className="detail-item-">
            <NumbersOutlinedIcon className="detail-icon-" style={{ color: 'green' }} />
            Patents: {item.patentCount}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {
            /* Rendering url icons */
            Object.keys(urlIcons).map(
              (key) =>
                item?.[key] && (
                  <span className="detail-item" key={key}>
                    <a
                      target="_blank"
                      href={
                        item[key].startsWith('http://') || item[key].startsWith('https://')
                          ? item[key]
                          : `https://${item[key]}`
                      }
                    >
                      {urlIcons[key]}
                    </a>
                  </span>
                )
            )
          }
        </div>

        {item?.homepage && (
          <Button
            variant="outlined"
            disableRipple={true}
            sx={activeButtonStyle}
            onClick={() => {
              const url = item.homepage;
              const prefixedUrl =
                url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
              window.open(prefixedUrl, '_blank');
            }}
          >
            Visit Homepage
          </Button>
        )}

        {item?.about && (
          <div style={{ padding: '0 10px' }}>
            <h2 className="detail-title">About</h2>
            <p style={{ margin: 0, textAlign: 'justify', whiteSpace: 'pre-line' }}>{item.about}</p>
          </div>
        )}
      </div>

      {item.source_patents && item.source_patents.length > 0 && (
        <>
          <h4>Vertical Specific Patents</h4>
          <div>
            {item.source_patents.map((patent, index) => (
              <Card
                key={index}
                category={'patents'}
                details={patent}
                cardHeader={'PATENT'}
                icons={icons}
                dotColor={'orange'}
                vertical_id={vertical_id}
                sublist={true}
                visibleBookmark={false}
              />
            ))}
          </div>
        </>
      )}

      {item.organic_results && item.organic_results.length > 0 && (
        <>
          <h4>All Patents</h4>
          <div>
            {item.organic_results.map((patent, index) => (
              <Card
                key={index}
                category={'patents'}
                details={patent}
                cardHeader={'PATENT'}
                icons={icons}
                dotColor={'orange'}
                vertical_id={vertical_id}
                depth={1}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
export default InventorPage;
